import moment from "moment";
import numeral from "numeral";
import { DEFAULT_TILE_STATE, getFilterIntermediateDatasets, getIsPinned, getListSortAscending, getListSortColumn } from "./reducer";
import { DATA_CHECKS, getDataset, getDefaultSuccess, getSuccessOptions } from "containers/Home/reducer";
import { getAiReadyMetrics, getCorrelationMetricsFromDataset } from "containers/Setup/reducer";
import { DATA_SOURCES, getSchemaMonitor } from "containers/SchemaMonitor/reducer";
import { DATA_SOURCES as LINK_DATA_SOURCES } from "components/View/DatasetLink";
import { getAccount } from "containers/Accounts/reducer";
import { chainComparators, getNumericComparator, getStringComparator, reverseOrder } from "utils/collect";
import AWSIcon from "images/EDG/DB/AWS.svg";
import AzureIcon from "images/EDG/DB/AZURE.svg";
import GCPIcon from "images/gcp.svg";
import CSVIconGray from "images/csv_light_grey.svg";
import { getSelectedFolder } from "containers/Folders/reducer";
import { getRawDatasets } from "containers/View/reducer";
import { getParentDatasetIds } from "utils/relation";
export function getTileState(state, id) {
    return state.sense.datasets[id] ?? DEFAULT_TILE_STATE;
}
export function getIsOpenAiReadiness(state, id) {
    return getTileState(state, id).isOpenAiReadiness;
}
export function getIsOpenLineage(state, id) {
    return getTileState(state, id).isOpenLineage;
}
export function getIsOpenDataValidation(state, id) {
    return getTileState(state, id).isOpenDataValidation;
}
export function getIsOpenSettings(state, id) {
    return getTileState(state, id).isOpenSettings;
}
export function getIsOpenOutcome(state, id) {
    return getTileState(state, id).isOpenOutcome;
}
export function getIsOpenInsights(state, id) {
    return getTileState(state, id).isOpenInsights;
}
export function getIsOpenDataEvaluated(state, id) {
    return getTileState(state, id).isOpenDataEvaluated;
}
export function getIsOpenTransformations(state, id) {
    return getTileState(state, id).isOpenTransformations;
}
export function getIsOpenTargetModelOptions(state, id) {
    return getTileState(state, id).isOpenTargetModelOptions;
}
export function getIsOpenKpiWhatsChangedOptions(state, id) {
    return getTileState(state, id).isOpenKpiWhatsChangedOptions;
}
export function getIsLoadingExplore(state, id) {
    return getTileState(state, id).isLoadingExplore;
}
export function getIsLoadingCreateAI(state, id) {
    return getTileState(state, id).isLoadingCreateAI;
}
export function getIsLoadingChat(state, id) {
    return getTileState(state, id).isLoadingChat;
}
export function getIsExpanded(state, id) {
    return getTileState(state, id).isExpanded;
}
export function getMinimumValueCount(state, id) {
    return getTileState(state, id).minimumValueCount;
}
export function getDateAnalysis(state, id) {
    return getTileState(state, id).dateAnalysis;
}
export function getOutcome(state, id) {
    return getTileState(state, id).outcome;
}
export function getDefaultOutcomeSuccess(state, datasetId) {
    const defaultSuccess = getDefaultSuccess(state, datasetId);
    const outcomeOptions = getOutcomeSuccessOptions(state, datasetId);
    return outcomeOptions?.find((o) => o?.value === defaultSuccess) ?? outcomeOptions?.[0];
}
export function getOutcomeSuccessOptions(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const successOptions = getSuccessOptions(state, datasetId, dataset, dataset?.outcome);
    const field = dataset?.fields?.[dataset?.outcome ?? ""];
    return field?.type !== "number" && field?.data
        ? successOptions?.map((o) => ({
            value: o.value,
            label: o.label
        }))
        : undefined;
}
export function getOutcomeSuccess(state, datasetId) {
    const { outcomeSuccess } = getOutcome(state, datasetId);
    if (outcomeSuccess) {
        return outcomeSuccess;
    }
    return getDefaultOutcomeSuccess(state, datasetId);
}
export function getListDateCreated(state, datasetId, isSort = false) {
    const dataset = getDataset(state, datasetId);
    if (dataset === undefined) {
        return "";
    }
    const created = moment.utc(dataset.created_date);
    return created.format(isSort ? "YYYY MM DD HH:mm:ss" : "MMM D YYYY");
}
export function getListDatasetOwner(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const retOwnerName = dataset?.created_by?.fullname;
    return retOwnerName ?? "";
}
export function getListDatasetName(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const isBoomi = dataset?.source.content.type == "CONNECTION_METADATA" && dataset?.source?.content?.provider === "BOOMI";
    const retDatasetName = isBoomi !== true || dataset?.name === undefined ? dataset?.name : dataset?.name.replace(".csv", "");
    return retDatasetName ?? "";
}
export function getListOutcomeLabel(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const outcomeValue = getOutcomeSuccess(state, datasetId);
    const isContinuous = dataset?.fields?.[dataset?.outcome ?? ""]?.type === "number";
    const retOutcomeLabel = dataset?.outcome
        ? dataset.outcome + (isContinuous || !outcomeValue ? "" : ": " + outcomeValue?.label)
        : "";
    return retOutcomeLabel;
}
export function getListColumnCount(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const isWideDatasetWarning = dataset?.wideDatasetWarning;
    const retColumnCount = dataset?.fields && Object.keys(dataset.fields).length > 0
        ? isWideDatasetWarning
            ? dataset.labels?.length
            : Object.keys(dataset.fields).length
        : "";
    return retColumnCount.toString();
}
export function getListRowCount(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const retRowCount = dataset?.number_of_rows !== undefined && dataset?.number_of_rows !== -1
        ? numeral(dataset?.number_of_rows).format(dataset?.number_of_rows < 1000 ? "0" : "0.0a")
        : "";
    return retRowCount;
}
export function getListPredictorCount(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const isReady = dataset?.number_of_rows !== -1;
    const isWideDatasetWarning = dataset?.wideDatasetWarning;
    const showActions = dataset?.outcome !== undefined && isReady && !isWideDatasetWarning;
    const aiReadyMetrics = getAiReadyMetrics(state, "new", datasetId);
    const predictors = [...(aiReadyMetrics?.aiReady ?? []), ...(aiReadyMetrics?.secondOrder?.aiReady ?? [])];
    const predictorCount = showActions ? predictors.length.toString() : "";
    return predictorCount;
}
export function getListInsights(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const isReady = dataset?.number_of_rows !== -1;
    const isWideDatasetWarning = dataset?.wideDatasetWarning;
    const showActions = dataset?.outcome !== undefined && isReady && !isWideDatasetWarning;
    const insightCount = showActions ? (dataset?.interaction_scores?.length ?? "") : "";
    return insightCount.toString();
}
export function getListDataSource(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const csvDataSource = DATA_SOURCES.find((source) => source?.value === "CSV");
    const sourceContent = dataset?.source?.content;
    let datasetIconGray = csvDataSource?.iconGray ?? CSVIconGray;
    let datasetTypeLabel = csvDataSource?.shortLabel ?? csvDataSource?.label;
    let datasetType = csvDataSource?.shortLabel ?? csvDataSource?.label;
    if (sourceContent?.type === "CONNECTION_METADATA") {
        const datasetSource = DATA_SOURCES.find((source) => source?.value === sourceContent?.provider) ??
            LINK_DATA_SOURCES.find((source) => String(source?.value).toUpperCase() === String(sourceContent?.provider).toUpperCase());
        if (datasetSource && "iconGray" in datasetSource && datasetSource?.iconGray) {
            datasetIconGray = datasetSource?.iconGray;
        }
        else {
            datasetIconGray = datasetSource?.image ?? datasetIconGray;
        }
        if (datasetSource && "shortLabel" in datasetSource && datasetSource?.shortLabel) {
            datasetTypeLabel = datasetSource?.shortLabel;
        }
        else {
            datasetTypeLabel = datasetSource?.label ?? datasetTypeLabel;
        }
        datasetType = datasetTypeLabel;
        if (sourceContent?.schema_monitor_id) {
            const schemaMonitor = getSchemaMonitor(state, sourceContent?.schema_monitor_id);
            if (schemaMonitor && schemaMonitor.name) {
                datasetTypeLabel = schemaMonitor.name;
            }
        }
    }
    else {
        const account = getAccount(state, dataset?.account_id);
        if (account) {
            datasetTypeLabel = account.name;
            datasetType = account.type;
            switch (account.type) {
                case "AWS":
                    datasetIconGray = AWSIcon;
                    break;
                case "AZURE":
                    datasetIconGray = AzureIcon;
                    break;
                case "GCP":
                    datasetIconGray = GCPIcon;
                    break;
            }
        }
    }
    if (datasetTypeLabel === undefined) {
        datasetTypeLabel = "";
    }
    if (datasetType === undefined) {
        datasetType = "";
    }
    return {
        datasetTypeLabel,
        datasetIconGray,
        datasetType
    };
}
export function getListDataReadiness(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const isError = dataset?.dataset_status === "ERROR";
    const isReady = dataset?.number_of_rows !== -1;
    const isWideDatasetWarning = dataset?.wideDatasetWarning;
    const showActions = dataset?.outcome !== undefined && isReady && !isWideDatasetWarning;
    const aiReadyMetrics = getAiReadyMetrics(state, "new", datasetId);
    const aiScore = (aiReadyMetrics?.aiReadinessScore ?? 0) * 10;
    const aiStatus = aiReadyMetrics?.aiReadinessStatus;
    const hasInteractionScores = dataset?.interaction_model_metrics !== undefined && isNaN(aiScore) !== true;
    const isWarning = hasInteractionScores !== true || aiStatus === "LOW_SIGNAL" || aiStatus === "SUSPECT_SIGNAL";
    const showBadge = !isError && showActions && hasInteractionScores;
    const dataReadinessLabel = showBadge ? aiScore.toPrecision(2) : "";
    return {
        showBadge,
        dataReadinessLabel,
        isWarning
    };
}
const VALIDATION_MESSAGES = [
    {
        message: ["too many values", "It would be discarded."],
        type: "high cardinality field",
        action: "would be ignored"
    },
    {
        message: ["It would be discarded."],
        type: "column",
        action: "would be ignored"
    },
    {
        message: ["missing values", "(", ")"],
        type: "column",
        action: "with missing values would be updated"
    },
    {
        message: ["too many values", "most informative values", "flagged and included in the modeling step"],
        type: "high cardinality field",
        action: "would be updated"
    },
    {
        message: [],
        code: "InvalidRows",
        type: "invalid row",
        action: "would be ignored"
    }
];
const checkMessage = (message, checkArr) => {
    if (!checkArr.length) {
        return false;
    }
    for (const x of checkArr) {
        if (!message.toLowerCase().includes(x.toLowerCase())) {
            return false;
        }
    }
    return true;
};
export const parseValidation = (updatedColumns) => {
    const retValidation = VALIDATION_MESSAGES.map((validation) => ({
        ...validation,
        value: []
    }));
    const extra = {
        message: [],
        type: "column",
        action: "would be updated for other reasons",
        value: []
    };
    const rowMessageIndex = VALIDATION_MESSAGES.findIndex((m) => m.code === "InvalidRows");
    updatedColumns.forEach((col) => {
        if (col.code === "InvalidRows") {
            const extraCount = col.metadata.extra_values_count;
            if (extraCount > 0) {
                let extraMessage = `${extraCount} row${extraCount !== 1 ? "s were" : " was"} ignored due to having too many columns.`;
                const rowList = col.metadata.rows_with_extra_values;
                if (rowList?.length > 0) {
                    extraMessage += ` Ignored row${extraCount !== 1 ? "s" : ""}: ${rowList.join(", ")}`;
                }
                retValidation[rowMessageIndex].value.push(extraMessage);
            }
            const missingCount = col.metadata.missing_values_count;
            if (missingCount > 0) {
                let missingMessage = `${missingCount} row${missingCount !== 1 ? "s were" : " was"} ignored due to having too few columns.`;
                const rowList = col.metadata.rows_with_missing_values;
                if (rowList?.length > 0) {
                    missingMessage += ` Ignored row${extraCount !== 1 ? "s" : ""}: ${rowList.join(", ")}`;
                }
                retValidation[rowMessageIndex].value.push(missingMessage);
            }
            const errorCount = col.metadata.error_count;
            if (errorCount > 0 && missingCount == 0 && extraCount == 0) {
                retValidation[rowMessageIndex].value.push(`${errorCount} row${errorCount !== 1 ? "s were" : " was"} ignored due to either having too many or too few columns.`);
            }
        }
        else {
            let found = false;
            const message = col.metadata && col.metadata.message ? col.metadata.message : col.message;
            for (const x of retValidation) {
                if (!found && checkMessage(message, x.message)) {
                    x.value.push(message);
                    found = true;
                    if (retValidation)
                        break;
                }
            }
            if (!found) {
                extra.value.push(message);
            }
        }
    });
    if (retValidation.length < 1) {
        extra.action = "would be updated";
    }
    retValidation.push(extra);
    return retValidation.filter((elem) => elem.value.length);
};
export function getListDataQuality(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const isProcessing = dataset?.status_name !== "DONE";
    const isError = dataset?.dataset_status === "ERROR";
    const isDataValidationReady = dataset?.status_name === "DONE" || dataset?.outcome !== undefined;
    const updatedColumns = (dataset && dataset.warnings) || [];
    const groupedUpdates = parseValidation(updatedColumns);
    const pNumber = isError
        ? 0
        : dataset?.status_name === "DONE" || dataset?.outcome
            ? DATA_CHECKS.length - groupedUpdates.length
            : 0;
    const dataQualityLabel = isError || isDataValidationReady ? pNumber + "/" + DATA_CHECKS.length : isProcessing ? "..." : "";
    const showBadge = !isError && isDataValidationReady;
    return {
        showBadge,
        dataQualityLabel,
        isError
    };
}
export function getListProblematicPredictors(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const isReady = dataset?.number_of_rows !== -1;
    const isWideDatasetWarning = dataset?.wideDatasetWarning;
    const showActions = dataset?.outcome !== undefined && isReady && !isWideDatasetWarning;
    const correlations = getCorrelationMetricsFromDataset(state, "new", datasetId);
    const problematicPredictors = correlations?.problematicPredictors;
    const problematicPredictorCount = showActions && problematicPredictors ? problematicPredictors.length : undefined;
    const hasProblematicPredictors = problematicPredictorCount !== undefined && problematicPredictorCount > 0;
    return {
        hasProblematicPredictors,
        problematicPredictorCount
    };
}
function getListSortValue(state, datasetId, sortColumnOverride) {
    const listSortColumn = sortColumnOverride ?? getListSortColumn(state);
    if (listSortColumn == "dataSource") {
        const listDataSource = getListDataSource(state, datasetId);
        return listDataSource.datasetType + "_" + listDataSource.datasetTypeLabel;
    }
    switch (listSortColumn) {
        case "datasetName":
            return getListDatasetName(state, datasetId);
        case "dataQuality":
            return getListDataQuality(state, datasetId).dataQualityLabel;
        case "outcomeLabel":
            return getListOutcomeLabel(state, datasetId);
        case "usefulVariables":
            return getListPredictorCount(state, datasetId);
        case "problematicPredictors":
            return (getListProblematicPredictors(state, datasetId).problematicPredictorCount ?? "").toString();
        case "insights":
            return getListInsights(state, datasetId);
        case "rows":
            return getListRowCount(state, datasetId);
        case "columns":
            return getListColumnCount(state, datasetId);
        case "dateCreated":
            return getListDateCreated(state, datasetId, true);
        case "owner":
            return getListDatasetOwner(state, datasetId);
        case "dataReadiness":
            return getListDataReadiness(state, datasetId).dataReadinessLabel;
    }
}
export function getSortedDatasetIdList(state, sortColumnOverride, isSortAscendingOverride) {
    const listSortAscending = isSortAscendingOverride ?? getListSortAscending(state);
    const datasets = getRawDatasets(state);
    const folderId = getSelectedFolder(state);
    const filterIntermediateDatasets = getFilterIntermediateDatasets(state);
    const intermediateDatasetIds = new Set();
    if (filterIntermediateDatasets) {
        datasets
            .flatMap((d) => {
            if (d.transformations.plan?.relation === undefined) {
                return [];
            }
            return getParentDatasetIds(d.transformations.plan?.relation);
        })
            .forEach((id) => intermediateDatasetIds.add(id));
    }
    const scoredDatasetIds = datasets
        .filter((d) => d.folder_id == folderId && !intermediateDatasetIds.has(d.id))
        .map((d) => ({
        datasetId: d.id,
        sortValue: getListSortValue(state, d.id, sortColumnOverride),
        isPinned: getIsPinned(state, d.id)
    }));
    const valueComparator = getStringComparator((x) => x.sortValue);
    const comparator = chainComparators([
        reverseOrder(getNumericComparator((x) => Number(x.isPinned))),
        listSortAscending ? valueComparator : reverseOrder(valueComparator)
    ]);
    return scoredDatasetIds.sort(comparator).map((x) => x.datasetId);
}
